import { type MouseEvent, useLayoutEffect, useRef, useState } from "react";
import type { LedgerFinding, LedgerRun } from "../../api/codeReview";
import type { PeterIssue } from "../../api/peterHandoff";
import {
  EFFORT_LEVELS,
  blobHref,
  findingRef,
  prOf,
  severityMeta,
  suggestionLines,
} from "../../lib/bobConsole";
import { fmtAgo, parseTs } from "../../lib/codeReviewFormat";
import { daysBetween } from "../../lib/bobReports";
import {
  REPRO_STATUSES,
  effortOf,
  headlineOf,
  ledgerTimeline,
  reproStatusOf,
  reproSteps,
  type LedgerEvent,
} from "../../lib/ledgerFormat";
import { peterStateFor } from "../../lib/peterHandoff";
import { absoluteTime } from "../../lib/time";
import PeterHandoff, { canSendToPeter, type OnFiled } from "../PeterHandoff";
import FindingActions from "./FindingActions";
import { STATE_LABELS, claimOf, locationOf } from "./FindingStream";
import type { RunNav } from "./reportNav";
import SeverityChip from "./SeverityChip";

export type PeterProps = {
  /** The branch Bob tracks, which an issue is filed against. */
  baseBranch: string | undefined;
  issues: readonly PeterIssue[];
  onFiled: OnFiled;
};

export interface DetailProps {
  finding: LedgerFinding;
  repo: string | undefined;
  branch: string | undefined;
  project: string;
  isAdmin: boolean;
  onChanged: () => Promise<void>;
  peter: PeterProps;
  related: readonly LedgerFinding[];
  onSelect: (id: number) => void;
  completedRun: LedgerRun | null;
  /** Links a history event to the run whose report lists it. Absent, the history names no run. */
  runs?: RunLinks;
}

export interface RunLinks extends RunNav {
  /** The run that reviewed this commit, or null when the loaded runs do not include one. */
  runAt: (sha: string) => number | null;
}

// A run's report lists a finding as new when it was first seen at that run's
// head, and as fixed when it was resolved there. Other events name no run.
const RUN_EVENTS = new Set(["found", "fixed"]);

function RunLink({ id, runs }: { id: number; runs: RunLinks }) {
  const open = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    runs.openRun(id);
  };
  return <a className="bob-ev-run" href={runs.runHref(id)} data-run={id} onClick={open}>Run {id}</a>;
}

function when(iso: unknown): string {
  return typeof iso === "string" ? absoluteTime(parseTs(iso)) : "";
}

function shortSha(sha: unknown): string | null {
  return typeof sha === "string" && sha !== "" ? sha.slice(0, 7) : null;
}

function commitHref(repo: string | undefined, sha: string): string | null {
  return repo ? `${encodeURI(`https://github.com/${repo}`)}/commit/${encodeURI(sha)}` : null;
}

function prHref(repo: string | undefined, pr: number): string | null {
  return repo ? `${encodeURI(`https://github.com/${repo}`)}/pull/${pr}` : null;
}

const REPRO_CHIPS: Readonly<Record<string, string>> = {
  predicted: "Predicted, derived from code",
  verified: "Verified in game",
  refuted: "Refuted in game",
};

function Meter({ level, tone }: { level: number; tone: string }) {
  return (
    <span className="bob-meter" aria-hidden="true">
      {[1, 2, 3].map((n) => <span key={n} data-on={n <= level ? tone : undefined} />)}
    </span>
  );
}

/** "Open 41 days" for an open finding, from when Bob first recorded it. */
function openFor(finding: LedgerFinding): string | null {
  if (finding.status !== "open" || typeof finding.created_at !== "string") return null;
  const days = daysBetween(finding.created_at, new Date().toISOString());
  if (days === null) return null;
  return days === 0 ? "Open since today" : `Open ${days} ${days === 1 ? "day" : "days"}`;
}

function DetailHead({ finding, repo, phone }: { finding: LedgerFinding; repo: string | undefined; phone: boolean }) {
  const status = typeof finding.status === "string" ? finding.status : "";
  const effort = effortOf(finding);
  const reproStatus = reproStatusOf(finding);
  const headline = headlineOf(finding);
  const message = typeof finding.message === "string" ? finding.message : "";
  const ledByImpact = headline !== message;
  const firstSeen = when(finding.created_at);
  const pr = prOf(finding);
  const prLink = pr === null ? null : prHref(repo, pr);
  // Bob's message as the headline is cut at three lines; when it is cut, the whole of it follows.
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [clamped, setClamped] = useState(false);
  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el || ledByImpact) return;
    const measure = () => setClamped(el.scrollHeight > el.clientHeight + 1);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [ledByImpact, headline]);
  return (
    <div className="bob-detail-head">
      <div className="bob-chiprow">
        <SeverityChip severity={finding.severity} />
        {typeof finding.category === "string" && finding.category !== "" && (
          <span className="bob-chip bob-chip-cat">{finding.category}</span>
        )}
        {effort && <span className="bob-chip bob-chip-effort">{effort} fix</span>}
        {STATE_LABELS[status] && (
          <span className="bob-chip bob-chip-status" data-state={status}>{STATE_LABELS[status]}</span>
        )}
        {reproStatus && (
          <span className="bob-chip bob-repro-badge" data-repro={reproStatus} title={REPRO_STATUSES[reproStatus]!.note}>
            {REPRO_CHIPS[reproStatus]}
          </span>
        )}
        <span className="bob-flex" />
        <span className="bob-mono bob-detail-id">
          #{finding.id}{firstSeen !== "" ? `, first seen ${firstSeen}` : ""}
        </span>
      </div>
      {ledByImpact && <span className="bob-eyebrow">What a player sees</span>}
      <h2 ref={titleRef} className={"bob-detail-title" + (ledByImpact ? "" : " is-message")} tabIndex={-1}>{headline}</h2>
      {phone && <span className="bob-mono bob-detail-loc">{locationOf(finding)}</span>}
      {phone && openFor(finding) && <span className="bob-detail-age">{openFor(finding)}</span>}
      {ledByImpact && message !== "" && (
        <p className="bob-found">
          <span className="bob-found-label">Bob found:</span> <span className="bob-found-msg">{message}</span>
        </p>
      )}
      {!ledByImpact && clamped && <p className="bob-found bob-found-full">{message}</p>}
      {pr !== null && (
        <p className="bob-detail-origin">
          From {prLink ? <a href={prLink} target="_blank" rel="noopener noreferrer">pull request #{pr}</a> : <>pull request #{pr}</>}
        </p>
      )}
    </div>
  );
}

/** Somebody pressed Solved on Bob's Discord card. A claim, never a state: the finding stays open. */
function SolvedClaim({ finding }: { finding: LedgerFinding }) {
  const claim = claimOf(finding);
  if (!claim) return null;
  return (
    <span
      className="bob-solved bob-claim"
      title="Somebody pressed Solved on Bob's Discord card. Bob has not checked it, so the finding stays open and comes back on his next pass if it was not handled after all."
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20 6 9 17l-5-5" />
      </svg>
      {claim}
    </span>
  );
}

function PeterNotSent({ finding, issues }: { finding: LedgerFinding; issues: readonly PeterIssue[] }) {
  if (peterStateFor(finding, issues).kind !== "none") return null;
  return (
    <span className="bob-peter-none">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" />
      </svg>
      Peter: not sent yet
    </span>
  );
}

function Axes({ finding }: { finding: LedgerFinding }) {
  const meta = severityMeta(finding.severity);
  const effort = effortOf(finding);
  const severity = typeof finding.severity === "string" ? finding.severity : "";
  return (
    <div className="bob-axes">
      <div className="bob-axis" data-axis="severity">
        <span className="bob-eyebrow">Severity</span>
        <div className="bob-axis-row">
          <span className={`bob-axis-value bob-sev-text-${meta ? meta.key.toLowerCase() : "other"}`}>{meta?.axis ?? severity}</span>
          {meta && <Meter level={meta.level} tone={meta.key.toLowerCase()} />}
        </div>
      </div>
      {effort && (
        <div className="bob-axis" data-axis="effort">
          <span className="bob-eyebrow">Effort</span>
          <div className="bob-axis-row">
            <span className="bob-axis-value">{effort}</span>
            <Meter level={EFFORT_LEVELS[effort] ?? 0} tone="ok" />
          </div>
        </div>
      )}
    </div>
  );
}

function Reproduction({ finding }: { finding: LedgerFinding }) {
  const status = reproStatusOf(finding);
  const steps = reproSteps(finding.repro_steps);
  if (status === null || steps.length === 0) return null;
  return (
    <section className="bob-section bob-repro" aria-label="Reproduction">
      <div className="bob-section-head">
        <h2>Reproduction</h2>
        <span className="bob-repro-note">{REPRO_STATUSES[status]!.note}</span>
      </div>
      <ol className="bob-steps">
        {steps.map((step, i) => (
          <li key={i}>
            <span className="bob-mono bob-step-n" aria-hidden="true">{i + 1}</span>
            <span className="bob-step-text">{step}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

function Evidence({ finding, repo, branch }: { finding: LedgerFinding; repo: string | undefined; branch: string | undefined }) {
  const ref = findingRef(finding, branch);
  const href = blobHref(repo, ref, finding);
  const loc = locationOf(finding);
  const at = typeof finding.last_seen_sha === "string" && finding.last_seen_sha.trim() !== "" ? shortSha(finding.last_seen_sha) : null;
  const explanation = typeof finding.explanation === "string" ? finding.explanation : "";
  return (
    <section className="bob-section bob-evidence" aria-label="Evidence">
      <h2>Evidence</h2>
      {explanation.trim() !== "" && <p className="bob-prose">{explanation}</p>}
      <div className="bob-evidence-loc">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <path d="M14 2v6h6" />
        </svg>
        {href ? (
          <a className="bob-mono bob-loc" href={href} target="_blank" rel="noopener noreferrer">{loc}</a>
        ) : (
          <span className="bob-mono bob-loc" title="no repo or branch on this answer, so the blob link could not be built">{loc}</span>
        )}
        {at && <span className="bob-mono bob-evidence-at">at {at}</span>}
      </div>
    </section>
  );
}

function SuggestedFix({ finding }: { finding: LedgerFinding }) {
  const lines = suggestionLines(finding);
  const [copied, setCopied] = useState(false);
  if (lines.length === 0) {
    return (
      <section className="bob-section" aria-label="Suggested fix">
        <h2>Suggested fix</h2>
        <p className="bob-prose bob-muted">
          Bob did not propose a replacement for this one, which he does only when the fix is a mechanical edit to the line
          above.
        </p>
      </section>
    );
  }
  const start = typeof finding.line === "number" ? finding.line : null;
  const copy = () => {
    const text = typeof finding.suggestion === "string" ? finding.suggestion : "";
    void navigator.clipboard?.writeText(text).then(() => setCopied(true), () => setCopied(false));
  };
  return (
    <section className="bob-section bob-patch" aria-label="Suggested fix">
      <div className="bob-section-head">
        <h2>Suggested fix</h2>
        <span className="bob-patch-counts">
          <span className="bob-patch-add">+{lines.length}</span>
          {start !== null && <span> at L{start}</span>}
        </span>
        <span className="bob-flex" />
        <button type="button" className="bob-btn bob-btn-small bob-copy" onClick={copy}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="9" y="9" width="12" height="12" rx="2" />
            <path d="M5 15V5a2 2 0 0 1 2-2h10" />
          </svg>
          {copied ? "Copied" : "Copy patch"}
        </button>
      </div>
      <div className="bob-code">
        <div className="bob-code-row bob-code-hunk">
          <span className="bob-ln" />
          <span />
          <span>@@ {locationOf(finding)} @@</span>
        </div>
        {lines.map((line, i) => (
          <div className="bob-code-row bob-row-add" key={i}>
            <span className="bob-ln">{start === null ? "" : start + i}</span>
            <span className="bob-add-mark">+</span>
            <span>{line}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function Related({ related, onSelect }: { related: readonly LedgerFinding[]; onSelect: (id: number) => void }) {
  if (related.length === 0) return null;
  return (
    <section className="bob-section" aria-label="Related findings">
      <h2>Related findings</h2>
      <div className="bob-related-list">
        {related.map((r) => (
          <button key={r.id} type="button" className="bob-related" data-related={r.id} onClick={() => onSelect(r.id)}>
            <SeverityChip severity={r.severity} />
            <span className="bob-mono bob-related-id">#{r.id}</span>
            <span className="bob-related-text">{headlineOf(r)}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function History(
  { events, repo, footer, runs }: { events: LedgerEvent[]; repo: string | undefined; footer: string | null; runs?: RunLinks },
) {
  return (
    <section className="bob-section" aria-label="History">
      <h2>History</h2>
      <ol className="bob-history">
        {events.map((ev, i) => {
          const sha = shortSha(ev.sha);
          const href = ev.sha ? commitHref(repo, ev.sha) : null;
          const at = ev.at ? when(ev.at) : "";
          const ago = ev.at ? fmtAgo(ev.at) : "";
          const runId = runs && ev.sha && RUN_EVENTS.has(ev.kind) ? runs.runAt(ev.sha) : null;
          return (
            <li className="bob-ev" data-kind={ev.kind} key={ev.kind}>
              <span className={"bob-tl-dot" + (i === 0 ? " is-first" : "")} data-kind={ev.kind} />
              <span className="bob-ev-body">
                <span className="bob-ev-title">{ev.title}</span>
                {(sha || at !== "") && (
                  <span className="bob-ev-meta">
                    {sha && (href ? <a className="bob-mono" href={href} target="_blank" rel="noopener noreferrer">{sha}</a> : <span className="bob-mono">{sha}</span>)}
                    {sha && at !== "" ? " · " : ""}
                    {at !== "" ? `${at} (${ago})` : ""}
                    {runId !== null && runs && <>{" · "}<RunLink id={runId} runs={runs} /></>}
                  </span>
                )}
                {ev.note !== null && <span className="bob-ev-note">{ev.note}</span>}
              </span>
            </li>
          );
        })}
      </ol>
      {footer && <p className="bob-muted bob-history-foot">{footer}</p>}
    </section>
  );
}

/** Everything about one finding. On a wide screen the actions sit in a bar under
 *  the headline; with `phone` the Peter hand-off moves into the body and the
 *  admin actions into a bottom bar. */
export default function FindingDetail(
  { finding, repo, branch, project, isAdmin, onChanged, peter, related, onSelect, completedRun, runs, phone = false }:
    DetailProps & { phone?: boolean },
) {
  const status = typeof finding.status === "string" ? finding.status : "";
  const [peterSlot, setPeterSlot] = useState<HTMLElement | null>(null);
  const sendable = canSendToPeter(finding, peter.issues);
  // On a phone the bottom bar holds the buttons and nothing else, and is left out when there is nothing to press.
  const bar = phone && (isAdmin || sendable);
  const lastReviewed = completedRun ? when(completedRun.created_at) : "";
  const footer = status === "open" && lastReviewed !== ""
    ? `Still open. Bob last reviewed ${branch ?? "this branch"} on ${lastReviewed}.`
    : null;
  const handoff = (
    <PeterHandoff
      project={project}
      finding={finding}
      baseBranch={peter.baseBranch}
      issues={peter.issues}
      onFiled={peter.onFiled}
      buttonSlot={bar ? peterSlot : undefined}
    />
  );
  const admin = isAdmin
    ? <FindingActions project={project} finding={finding} onChanged={onChanged} layout={phone ? "bar" : "inline"} />
    : null;

  return (
    <div className="bob-detail-body" data-detail={finding.id}>
      <DetailHead finding={finding} repo={repo} phone={phone} />
      {phone ? (
        <div className="bob-peter-card">
          <SolvedClaim finding={finding} />
          {handoff}
          <PeterNotSent finding={finding} issues={peter.issues} />
        </div>
      ) : (
        <div className="bob-actionbar" data-actions="">
          {handoff}
          <SolvedClaim finding={finding} />
          {admin}
          <span className="bob-flex" />
          <PeterNotSent finding={finding} issues={peter.issues} />
        </div>
      )}
      <Axes finding={finding} />
      <Reproduction finding={finding} />
      <Evidence finding={finding} repo={repo} branch={branch} />
      <SuggestedFix finding={finding} />
      <Related related={related} onSelect={onSelect} />
      <History events={ledgerTimeline(finding)} repo={repo} footer={footer} runs={runs} />
      {bar && (
        <nav className="bob-phone-actions" aria-label="Finding actions" data-actions="">
          {sendable && <span className="bob-bar-slot" ref={setPeterSlot} />}
          {admin}
        </nav>
      )}
    </div>
  );
}
